/**
 * Filters a state value by selected states (for state column filtering).
 * @param {*} value - Single value or array of state values
 * @param {Array} selectedStates - Selected state objects with id
 * @returns {*} Filtered value(s)
 */
export const filterStateValueBySelection = (value, selectedStates) => {
  if (!selectedStates?.length || value == null) {
    return value;
  }
  const selectedIds = new Set(
    selectedStates.map((s) => s?.id).filter((id) => id != null && id !== '')
  );
  if (selectedIds.size === 0) {
    return value;
  }
  const arr = Array.isArray(value) ? value : [value];
  const filtered = arr.filter((v) => {
    const id = typeof v === 'object' && v?.id != null ? String(v.id) : String(v);
    return selectedIds.has(id);
  });
  return Array.isArray(value) ? filtered : (filtered[0] ?? value);
};

/**
 * Checks if a column is a state column based on property or fieldName.
 * @param {Object} column - Column definition
 * @returns {Boolean}
 */
export const isStateColumn = (column) => {
  const p = (column.property || '').toLowerCase();
  const fn = (column.fieldName || '').toLowerCase();
  return p === 'states' || p === 'state' || fn.includes('states') || fn.includes('state');
};

/**
 * Resolves a cell value from fieldsMap using property and optional alternate field names.
 * @param {Object} fieldsMap - Map of fieldName -> value from position.fields
 * @param {String} property - Primary field name (column.property)
 * @param {String} [fieldName] - Optional pipe-delimited alternate names, e.g. "payer|pbmClient"
 * @returns {*} The first non-null/undefined value found, or undefined
 */
export const resolveFieldValueFromMap = (fieldsMap, property, fieldName) => {
  const candidates = fieldName
    ? [property, ...fieldName.split('|').map((f) => f.trim()).filter(Boolean)]
    : [property];
  // Deduplicate while preserving order (property first)
  const unique = [...new Set(candidates)];
  for (const name of unique) {
    const value = fieldsMap[name];
    if (value !== undefined && value !== null) {
      return value;
    }
  }
  return undefined;
};


/**
 * Creates a consistent key from fieldName (string or pipe-delimited string)
 * Supports formats: "payer" or "payer|pbmClient"
 * @param {String} fieldName - Field name(s) as string
 * @returns {String} Key like "payer" or "pbmClient|payer"
 */
export const getFieldNameKey = (fieldName) => {
  if (!fieldName) {
    return '';
  }

  // Handle pipe-delimited string or single string
  const fields = String(fieldName).split('|').map((f) => f.trim()).filter(Boolean);
  return fields.sort().join('|');
}

/**
 * Extracts unique filter options from coverage positions for a dynamic field
 * Supports formats: "payer" or "payer|pbmClient"
 * @param {Array} coveragePositions - Array of coverage positions
 * @param {String} fieldName - The fieldName(s) to extract values for (pipe-delimited string)
 * @returns {Array} Array of {id, label} objects for filter options
 */
export const extractDynamicFieldOptions = (coveragePositions, fieldName, filterLayout = []) => {
  const valueMap = new Map();

  // Normalize to array: handle pipe-delimited string or single string
  if (!fieldName) {
    return []; // Return empty array if fieldName is null/undefined/empty
  }

  const fieldNames = String(fieldName).split('|').map((f) => f.trim()).filter(Boolean);
  const filterEntry = filterLayout.find(
    (f) => f.filter === 'dynamic' && fieldNames.includes(f.fieldName),
  );
  const mappings = filterEntry?.mappings || {};

  // Return empty array if no valid field names after normalization
  if (fieldNames.length === 0) {
    return [];
  }

  // Helper functions to process values
  const getValueKey = (value) => typeof value === 'object' && value !== null
    ? JSON.stringify(value)
    : String(value);

  const getValueLabel = (value) => typeof value === 'object' && value !== null
    ? (value.label || value.name || String(value))
    : String(value);

  // Iterate through each coverage position to extract unique field values
  coveragePositions.forEach((position) => {
    // Skip positions that don't have a fields array
    if (!position.fields || !Array.isArray(position.fields)) {
      return;
    }

    // Check each field name (supports multiple fields via pipe-delimited string)
    fieldNames.forEach((name) => {
      // Find the field matching the current field name
      const field = position.fields.find((f) => f.fieldName === name);
      // Skip if field not found or has no value
      if (!field?.value) {
        return;
      }

      // Normalize field value to array (handles both single values and arrays)
      const values = Array.isArray(field.value) ? field.value : [field.value];
      // Process each value in the field
      values.forEach((value) => {
        const key = getValueKey(value);
        // Add to map if not already present (ensures uniqueness)
        if (!valueMap.has(key)) {
          const rawLabel = getValueLabel(value);
          valueMap.set(key, {id: key, label: mappings[rawLabel] ?? rawLabel, value});
        }
      });
    });
  });

  return Array.from(valueMap.values()).sort((a, b) =>
    a.label.localeCompare(b.label)
  );
}

/**
 * Checks if a position matches any of the given fieldNames with selectedValues
 * @param {Object} position - Coverage position
 * @param {Array<String>} fieldNames - Field names to check
 * @param {Array} selectedValues - Selected filter values
 * @returns {Boolean} True if position matches
 */
const matchesFieldValue = (position, fieldNames, selectedValues) => {
  // Skip positions that don't have a fields array
  if (!position.fields || !Array.isArray(position.fields)) {
    return false;
  }

  // Check if any field name matches
  return fieldNames.some((fieldName) => {
    // Find the field matching the current field name
    const field = position.fields.find((f) => f.fieldName === fieldName);
    // Skip if field not found or has no value
    if (!field || field.value === undefined || field.value === null) {
      return false;
    }

    // Normalize field value to array and convert to keys for comparison
    const fieldValues = Array.isArray(field.value) ? field.value : [field.value];
    const fieldValueKeys = fieldValues.map((v) =>
      typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)
    );

    // Check if any selected value matches the field values
    return selectedValues.some((selected) => {
      // Extract key from selected value (prefer id if object, otherwise stringify)
      const selectedKey = typeof selected === 'object' && selected.id !== undefined
        ? selected.id
        : String(selected);
      return fieldValueKeys.includes(selectedKey);
    });
  });
}

/**
 * Applies dynamic column filters to filtered positions
 * Handles fieldNameKey as pipe-delimited string (e.g., "payer|pbmClient")
 * @param {Array} filteredPositions - Already filtered positions
 * @param {Object} selectedDynamicFilters - {fieldNameKey: [selectedValues]}
 * @param {Object} settings - Settings object
 * @returns {Array} Filtered positions
 */
export const applyDynamicFilters = (filteredPositions, selectedDynamicFilters, settings) => {
  if (!settings.table.dynamicColumns || !selectedDynamicFilters || Object.keys(selectedDynamicFilters).length === 0) {
    return filteredPositions;
  }

  return filteredPositions.filter((position) => {
    return Object.entries(selectedDynamicFilters).every(([fieldNameKey, selectedValues]) => {
      if (!selectedValues || selectedValues.length === 0) {
        return true;
      }

      // Parse key: split if contains '|', otherwise single field
      // Supports keys like "payer" or "payer|pbmClient"
      const fieldNames = fieldNameKey.includes('|')
        ? fieldNameKey.split('|')
        : [fieldNameKey];

      return matchesFieldValue(position, fieldNames, selectedValues);
    });
  });
}

